import React, { Key, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AdminFormSection, ConditionalRender } from '@ui';
import { CreateTXUserFormValues } from '@/types/customer';
import { TXUserRoleCreateForm } from '../TXRoleForm';
import { TXCenterSingleSelect } from '../components/selectors/TXCenterSingleSelect';
import { SelectItemType } from '@/types';
import { TXCenter } from '@/api/apis/facilities';

export type TXCenterRoleFormProps = {
  isReadOnly?: boolean;
};

export const TXUserCreateForm = React.memo(({ isReadOnly = false }: TXCenterRoleFormProps) => {
  const { control, trigger, setValue } = useFormContext<CreateTXUserFormValues>();

  const txCenterName = useWatch({ control, name: 'txCenterName' }) as string;
  const txCenterId = useWatch({ control, name: 'txCenterId' }) as string;

  const handleSetValue = useCallback(
    (value: Key | null, infiniteDropdownTxCentersList: unknown[]) => {
      setValue('txCenterId', value?.toString() ?? '', { shouldValidate: true });
      const list = infiniteDropdownTxCentersList as (SelectItemType & { data?: TXCenter })[];
      const txCenter = list.find((h) => h.value === value?.toString());
      if (txCenter) {
        setValue('txCenterName', txCenter.name, { shouldValidate: true });
        setValue('allowedEmailDomains', txCenter.data?.allowedEmailDomains || []);
      } else {
        setValue('txCenterName', '');
      }
    },
    [setValue],
  );

  const handleSelectionChange = useCallback(
    (key: Key | null, onChange: (val: Key | null) => void) => {
      onChange(key);
      trigger('email');

      if (!key) {
        setValue('txCenterId', '');
        setValue('txCenterName', '');
        setValue('roles', [], { shouldValidate: true, shouldDirty: true });
        setValue('permissions', [], { shouldValidate: true, shouldDirty: true });
      }
    },
    [setValue, trigger],
  );

  return (
    <AdminFormSection title="TX Center" isReadOnly={isReadOnly} type="user">
      <TXCenterSingleSelect
        isReadOnly={isReadOnly}
        txCenterName={txCenterName}
        onSelectionChangeAction={handleSelectionChange}
        setValueAction={handleSetValue}
      />
      <ConditionalRender show={!!txCenterId}>
        <TXUserRoleCreateForm isReadOnly={isReadOnly} />
      </ConditionalRender>
    </AdminFormSection>
  );
});

TXUserCreateForm.displayName = 'TXUserCreateForm';
